import React, { useEffect } from 'react';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import withStyles from 'isomorphic-style-loader/withStyles';
import gql from 'graphql-tag';
import s from './TaxContainer.css';
import Loader from '../../../components/Common/Loader/Loader';
import { Form, InputGroup } from 'react-bootstrap';
import { Field, reduxForm, change } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import cx from 'classnames';


const GET_GRANT_TOTAL_TAX = gql`
  query getGrandTotalTax {
    getGrandTotalTax {
      status
      errorMessage
      result {
        totalTax
        serviceFee
        taxCalculationType
      }
    }
  }
`;

const UPDATE_TAX = gql`
  mutation updateTax($tax: Float!, $serviceFee: Float!, $taxCalculationType: String!) {
    updateTax(tax: $tax, serviceFee: $serviceFee, taxCalculationType: $taxCalculationType) {
      status
      errorMessage
      result {
        totalTax
        serviceFee
        taxCalculationType
    }
    }
  }
`;

const renderTaxField = ({ input, label, type, meta: { touched, error }, style }) => {
    return (
        <div className={s.paddingRoutesSection}>
            <Form.Group>
                <Form.Label>{label}</Form.Label>
                <InputGroup style={{ maxWidth: '900px' }}>
                    <Form.Control
                        className='py-4 fs-5'
                        {...input}
                        placeholder="Enter tax percentage"
                        type={type}
                        style={{ maxWidth: '300px' }}
                    />
                    <InputGroup.Append>
                        <InputGroup.Text className="bg-success text-white" style={{ padding: "0px 20px" }}> % </InputGroup.Text>
                    </InputGroup.Append>
                </InputGroup>
                {touched && error && <span className={s.errorMessage}>{error}</span>}
            </Form.Group>
        </div>
    );
};

const renderServiceFeeField = ({ input, label, type, meta: { touched, error }, style }) => {
    return (
        <div className={s.paddingRoutesSection}>
            <Form.Group>
                <Form.Label>{label}</Form.Label>
                <InputGroup style={{ maxWidth: '900px' }}>
                    <Form.Control
                        className='py-4 fs-5'
                        {...input}
                        placeholder="Enter service fee percentage"
                        type={type}
                        style={{ maxWidth: '300px' }}
                    />
                    <InputGroup.Append>
                        <InputGroup.Text className="bg-primary text-white" style={{ padding: "0px 20px" }}> % </InputGroup.Text>
                    </InputGroup.Append>
                </InputGroup>
                {touched && error && <span className={s.errorMessage}>{error}</span>}
            </Form.Group>
        </div>
    );
};

const renderTaxCalculationTypeField = ({ input, label, meta: { touched, error } }) => {
    return (
        <div className={s.paddingRoutesSection}>
            <label>{label}</label>
            <Field name="taxCalculationType" className={cx(s.formControlSelect, s.formControlInputStore, 'formControlInputStoreRTL')} component="select">
                <option value="SERVICE_FEE_FIRST">Service Fee First</option>
                <option value="TAX_FIRST">Tax First</option>
            </Field>
        </div>
    );
};

const TaxContainer = ({
    getGrandTotalTax: { loading, getGrandTotalTax },
    mutate,
    handleSubmit,
    submitting,
    invalid,
    dispatch
}) => {
    useEffect(() => {
        if (getGrandTotalTax?.result?.totalTax !== undefined) {
            dispatch(change('TaxForm', 'tax', getGrandTotalTax.result.totalTax));
        }
        if (getGrandTotalTax?.result?.serviceFee !== undefined) {
            dispatch(change('TaxForm', 'serviceFee', getGrandTotalTax.result.serviceFee));
        }
        if (getGrandTotalTax?.result?.taxCalculationType !== undefined) {
            console.log('Setting taxCalculationType:', getGrandTotalTax.result.taxCalculationType); // Debug log
            dispatch(change('TaxForm', 'taxCalculationType', getGrandTotalTax.result.taxCalculationType));
        }
    }, [getGrandTotalTax, dispatch]);

    if (loading) {
        return <Loader type={"page"} show={loading}></Loader>
    }

    const onSubmit = async (values) => {
        try {
            const response = await mutate({
                variables: {
                    tax: parseFloat(values.tax),
                    serviceFee: parseFloat(values.serviceFee),
                    taxCalculationType: values.taxCalculationType
                },
                refetchQueries: [
                    {
                        query: GET_GRANT_TOTAL_TAX
                    }
                ]
            });

            if (response && response.data && response.data.updateTax) {
                const { updateTax } = response.data;

                if (updateTax.result && updateTax.result.totalTax !== undefined) {
                    toastr.success('Success!', 'Tax settings updated successfully!');
                } else {
                    toastr.error('Error!', updateTax.errorMessage || 'Failed to update tax settings');
                }
            } else {
                toastr.error('Error!', 'Invalid response from server');
            }
        } catch (error) {
            console.error('Error in onSubmit:', error);
            toastr.error('Error!', 'Failed to update tax settings. Please try again.');
        }
    };

    return (
        <div className="p-4 container pb-5 mt-5" >
            <div className={"bg-white p-4 mt-5 mt-md-0"} style={{ borderRadius: "20px", marginTop: "50px" }}>
                <h3 className='mb-4'>
                    Manage your tax
                </h3>
                <form onSubmit={handleSubmit(onSubmit)}>

                    <Field
                        className='form-control size-lg'
                        name="serviceFee"
                        type="number"
                        label="Service Fee"
                        component={renderServiceFeeField}
                        style={{ maxWidth: '200px' }}
                    />
                    <Field
                        className='form-control size-lg'
                        name="tax"
                        type="number"
                        label="Government Tax"
                        component={renderTaxField}
                        style={{ maxWidth: '200px' }}
                    />

                    <Field
                        className='form-control size-lg'
                        name="taxCalculationType"
                        label="Tax Calculation Type"
                        component={renderTaxCalculationTypeField}
                    />

                    <button className='btn btn-danger' type="submit" disabled={submitting || invalid}>
                        {submitting ? 'Saving...' : 'Save Tax'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const validate = (values) => {
    const errors = {};
    if (values.tax) {
        if (isNaN(values.tax) || Number(values.tax) < 0 || Number(values.tax) > 100) {
            errors.tax = 'Please enter a valid tax percentage between 0 and 100';
        }
    }
    if (values.serviceFee) {
        if (isNaN(values.serviceFee) || Number(values.serviceFee) < 0 || Number(values.serviceFee) > 100) {
            errors.serviceFee = 'Please enter a valid service fee percentage between 0 and 100';
        }
    }
    if (values.taxCalculationType === '') {
        errors.taxCalculationType = 'Please select a tax calculation type';
    }
    return errors;
};

export default compose(
    withStyles(s),
    reduxForm({
        form: 'TaxForm',
        validate,
        enableReinitialize: true,
        initialValues: props => ({
            tax: props.getGrandTotalTax?.result?.totalTax || '',
            serviceFee: props.getGrandTotalTax?.result?.serviceFee || '',
            taxCalculationType: props.getGrandTotalTax?.result?.taxCalculationType || ''
        })
    }),
    graphql(GET_GRANT_TOTAL_TAX, {
        name: 'getGrandTotalTax',
        options: {
            fetchPolicy: 'network-only',
            ssr: false
        }
    }),
    graphql(UPDATE_TAX, {
        name: 'mutate'
    })
)(TaxContainer);
